import { z } from 'zod';
import { DisciplineSchema, PricingTierSchema, ProjectStatusSchema } from './common';

export const PROJECT_TYPES = [
  'NEW_CONSTRUCTION', 'RENOVATION', 'TENANT_IMPROVEMENT', 'DATA_CENTER',
  'HEALTHCARE', 'EDUCATION', 'GOVERNMENT', 'RETAIL', 'HOSPITALITY', 'INDUSTRIAL',
] as const;
export const ProjectTypeSchema = z.enum(PROJECT_TYPES);
export type ProjectType = z.infer<typeof ProjectTypeSchema>;

export const ProjectSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  name: z.string(),
  type: ProjectTypeSchema,
  status: ProjectStatusSchema,
  location: z.string().nullable(),
  jurisdiction: z.string().nullable(),
  disciplines: z.array(DisciplineSchema),
  pricingTier: PricingTierSchema,
  regionalMultiplier: z.number(),
  bidDueDate: z.string().nullable(),
  sourceOpportunityId: z.string().nullable(),
  submittedAt: z.string().nullable().optional(),
  submittedBy: z.string().nullable().optional(),
  submissionVersionId: z.string().nullable().optional(),
  submissionR2Key: z.string().nullable().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Project = z.infer<typeof ProjectSchema>;

// Base object — keep as ZodObject (not ZodEffects) so UpdateProjectSchema can
// call .partial() on it. The designBuild/serviceContract mutex is layered on
// top via .refine() when used for CreateProjectSchema validation.
const CreateProjectBase = z.object({
  name: z.string().min(1).max(256),
  type: ProjectTypeSchema.default('NEW_CONSTRUCTION'),
  location: z.string().max(256).optional(),
  jurisdiction: z.string().max(128).optional(),
  disciplines: z.array(DisciplineSchema).min(1),
  pricingTier: PricingTierSchema.default('MID'),
  regionalMultiplier: z.number().positive().default(1.0),
  bidDueDate: z.string().datetime().optional(),
  sourceOpportunityId: z.string().optional(),
  // Phase 15.0 — design-build flag. When true, POST /projects upserts a
  // project_fact_sheets row with design_build=1, which gates the design-
  // build-aware classifier (15.1), symbol-detection discipline gate (15.2),
  // and Synthesis→BOM stub promoter (15.3). Default unset = bid-build.
  designBuild: z.boolean().optional(),
  // Phase 43.0 — service-contract flag. When true, POST /projects upserts
  // a project_fact_sheets row with service_contract=1, which gates Inspector
  // Ivan (43.2), SA Composer (43.3), and the service-RFP Sarah pass (43.4).
  // Mutex with designBuild — server rejects 400 if both are true.
  serviceContract: z.boolean().optional(),
});

export const CreateProjectSchema = CreateProjectBase.refine(
  (data) => !(data.designBuild === true && data.serviceContract === true),
  { message: 'designBuild and serviceContract are mutually exclusive', path: ['serviceContract'] },
);
export type CreateProject = z.infer<typeof CreateProjectSchema>;

export const UpdateProjectSchema = CreateProjectBase.partial().extend({
  status: ProjectStatusSchema.optional(),
});
export type UpdateProject = z.infer<typeof UpdateProjectSchema>;

export const ProjectListQuerySchema = z.object({
  status: ProjectStatusSchema.optional(),
  discipline: DisciplineSchema.optional(),
  search: z.string().max(128).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type ProjectListQuery = z.infer<typeof ProjectListQuerySchema>;
